import {
  Component,
  DestroyRef,
  Input,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterModule } from '@angular/router';
import {
  IonicModule,
  ModalController,
  Platform,
  IonNav,
  NavParams,
  MenuController,
} from '@ionic/angular';
import { Subscription } from 'rxjs';
import { InputConfigService, InputValidator } from 'src/app/core/services/input-config.service';
import { UiUtilityService } from 'src/app/core/services/ui-utility.service';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { InputErrorComponent } from 'src/app/shared/components/input-error/input-error.component';
import { deviceDetailsInputConfig } from './device-details-input.config';
import { UnifiedInput } from 'src/app/shared/components/unified-custom-input/unified-input.types';
import { UnifiedCustomInputComponent } from 'src/app/shared/components/unified-custom-input/unified-custom-input.component';
import { cloneDeep } from 'lodash-es';
import { UnifiedSelectComponent } from 'src/app/shared/components/unified-select/unified-select.component';
import { SettingsService } from 'src/app/features/settings/services/settings.service';
import { UnifiedHeaderComponent } from 'src/app/shared/components/unified-header/unified-header.component';
import { DashboardService } from 'src/app/features/dashboard/services/dashboard.service';
import { Capacitor } from '@capacitor/core';
import { AccessControlDirective } from 'src/app/shared/directives/access-control.directive';
import { AuthService } from 'src/app/core/services/auth.service';
import { TOAST } from 'src/app/shared/config/app.constant';
import { DeviceManagmentService } from '../device-management.service';
import { HeaderService } from 'src/app/core/services/header.service';
import { RoomDetailsPage } from '../../settings/room-management/room-details/room-details.page';
import { BedDetailsPage } from '../../bed-management/pages/bed-details/bed-details.page';
import { WearerDetailsPage } from '../../wearer-management/pages/wearer-details/wearer-details.page';

@Component({
  standalone: true,
  selector: 'app-device-details',
  templateUrl: './device-details.page.html',
  styleUrls: ['./device-details.page.scss'],
  imports: [
    CommonModule,
    IonicModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    InputErrorComponent,
    UnifiedCustomInputComponent,
    UnifiedHeaderComponent,
    AccessControlDirective
  ],
})
export class DeviceDetailsPage implements OnInit, OnDestroy {
  @Input('serviceId') serviceId = '';
  @Input('serviceName') serviceName = '';
  macId: string = '';
  isRootPage: boolean = false;
  mode: string = 'detail';
  destroyRef = inject(DestroyRef);
  deviceDetails: any = null;
  deviceDetailsForm: FormGroup | null = null;
  isSaveDisabled = true;
  subscriptions: Subscription = new Subscription();
  validator: InputValidator | null = null;
  deviceDetailsInputConfig: UnifiedInput[] = cloneDeep(deviceDetailsInputConfig);
  backButtonClicked: boolean = false;
  isReadMode = true;
  deviceTypes: any[] = [];
  rooms: any[] = [];
  bedList: any[] = [];
  subscription: any;
  isNative = Capacitor.isNativePlatform();
  isRoomSummaryEnabled = true;
  isWearerSummaryEnabled = true;
  isBedSummaryEnabled = true;
  select: any = {};
  placementTypes = [{ name: '', displayName: 'Unassigned' }, { name: 'bedslot', displayName: 'Bed Slot' }, { name: 'bed', displayName: 'Bed' }];
  isBedSlotEnabled = false;

  constructor(
    private deviceService: DeviceManagmentService,
    private formBuilder: FormBuilder,
    private uiUtilityService: UiUtilityService,
    private modalCtrl: ModalController,
    private settingsService: SettingsService,
    private platform: Platform,
    private navParams: NavParams,
    private nav: IonNav,
    private menuCtrl: MenuController,
    private dashboardService: DashboardService,
    private authService: AuthService,
    private inputConfigService: InputConfigService,
    private headerService: HeaderService
  ) { }

  ngOnInit(): void {
    this.isBedSlotEnabled = this.headerService.isBedSlotEnabled;
    const macId = this.navParams.get('macId');
    const mode = this.navParams.get('mode');
    this.didOnInit(macId, mode);
    this.isRoomSummaryEnabled = this.authService.isFeatureEnabled('getRoomDetail');
    this.isWearerSummaryEnabled = this.authService.isFeatureEnabled('getWearerDetail');
    this.isBedSummaryEnabled = this.authService.isFeatureEnabled('getBedDetail');
  }

  didOnInit(macId: string, mode: string) {
    this.macId = macId;
    this.mode = mode;
    this.isReadMode = this.mode === 'edit' ? false : true;
  }

  ionViewDidEnter() {
    this.subscription = this.platform.backButton.subscribeWithPriority(
      10,
      () => {
        console.log('Hardware back clicked');
      }
    );
  }

  ionViewWillLeave() {
    this.subscription.unsubscribe();
  }

  didViewWillEnter() {
    this.isSaveDisabled = true;
    if (this.macId) {
      this.getDeviceDetails();
    } else {
      this.setConfig('detail', true);
      const defaultValues = this.uiUtilityService.getValuesFromUnifiedInput(
        deviceDetailsInputConfig
      );
      this.createForm(defaultValues);
      this.subscribeForm();
    }
  }

  subscribeForm() {
    this.deviceDetailsForm?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.deviceDetailsForm) {
          this.isSaveDisabled =
            this.deviceDetailsForm.invalid || !this.deviceDetailsForm.dirty;
        }
      });
  }

  ionViewWillEnter() {
    this.didViewWillEnter();
  }

  setDeviceDetails(data: any) {
    this.deviceDetails = data;
    if (this.deviceDetails?.macId) {
      this.setConfig('', '', this.deviceDetails);
      this.setConfig('detail', !this.isReadMode);
    }
    this.createForm(data);
    this.subscribeForm();
    this.isSaveDisabled = false;
  }

  async getDeviceDetails() {
    await this.uiUtilityService.showLoader();
    this.deviceService
      .getDeviceDetails(this.serviceId, this.macId)
      .subscribe((response: any) => {
        if (response && response.success && response.data) {
          let placementType = '';
          let placementTypeName = 'Unassigned';
          if (response.data?.roomId && response.data?.bedSlotId) {
            placementTypeName = 'Bed Slot';
            placementType = 'bedslot';
          } else if (response.data?.bedId) {
            placementTypeName = 'Bed';
            placementType = 'bed';
          }
          response.data['placementTypeName'] = placementTypeName;
          response.data['placementType'] = placementType;
          this.setDeviceDetails(response.data);
        } else {
          const header = 'Error';
          this.uiUtilityService.showAlert(response.errorMessage!, header);
        }
      });
  }

  setConfig(key: string, value: any, data?: any) {
    this.deviceDetailsInputConfig.forEach((item: any) => {
      if (data) {
        item.value = data[item.name];
      } else {
        item[key] = value;
      }
      if (item?.name === 'macId' && this.deviceDetails?.macId) {
        item.detail = false;
      }
    });
  }

  createForm(deviceDetails: any) {
    const { localId, firstName, lastName } = deviceDetails;
    const resident = {
      localId, firstName, lastName
    };
    this.deviceDetailsForm = this.formBuilder.group({
      macId: [deviceDetails?.macId || '', [Validators.required]],
      modelId: [deviceDetails?.modelId || '', [Validators.required]],
      modelName: [deviceDetails?.modelName || ''],
      placementType: [deviceDetails?.placementType || ''],
      placementTypeName: [deviceDetails?.placementTypeName || ''],
      roomName: [deviceDetails?.roomName],
      roomId: [deviceDetails?.roomId],
      bedSlotId: [deviceDetails?.bedSlotId],
      slotName: [deviceDetails?.slotName],
      bedId: [deviceDetails?.bedId],
      bedName: [deviceDetails?.bedName],
      residentName: [resident ? this.uiUtilityService.getEntityDisplayName(resident) : ''],
    });
  }

  inputChanged(inputConfig: UnifiedInput) {
    if (this.deviceDetailsForm && inputConfig.name) {
      const found = this.deviceDetailsInputConfig.find(
        (item: any) => item.name === inputConfig.name
      );
      if (found) {
        found.value = inputConfig.value;
        this.deviceDetailsForm.markAsDirty();
        this.deviceDetailsForm.controls[inputConfig.name].patchValue(
          inputConfig.value
        );
      }
    }
  }

  async showSelectModal({
    title,
    emptyMessage,
    key,
    displayName,
    value,
    list,
    isSave,
    header,
    rightSlot
  }: any) {
    const modal = await this.modalCtrl.create({
      component: UnifiedSelectComponent,
      componentProps: {
        data: { title, emptyMessage, key, displayName, value, list, header, rightSlot },
        isSave,
      },
    });
    modal.present();
    return await modal.onWillDismiss();
  }

  async didSelectDeviceModal() {
    const modelId = this.select.hasOwnProperty('modelId') ? this.select.modelId : this.deviceDetails?.modelId;
    const { data, role } = await this.showSelectModal({
      title: 'Select Type',
      emptyMessage: 'No model types available right now.',
      key: 'id',
      displayName: 'label',
      value: modelId || '',
      list: this.deviceTypes,
    });
    if (role === 'confirm') {
      this.deviceDetailsForm?.markAsDirty();
      const device = this.deviceTypes.find(
        (item: any) => item.id === data
      );
      if (device) {
        // this.selectedDeviceTypeId = data;
        this.deviceDetailsForm?.controls['modelName'].patchValue(
          device.label
        );
        this.deviceDetailsForm?.controls['modelId'].patchValue(
          device.id
        );
        this.select['modelId'] = device.id;
      } else {
        this.select['modelId'] = null;
      }
    }
  }

  async setDeviceType() {
    if (this.macId && this.isReadMode) {
      return;
    }
    if (this.deviceTypes.length) {
      this.didSelectDeviceModal();
    } else {
      await this.uiUtilityService.showLoader();
      this.deviceService
        .getDeviceTypes(this.serviceId)
        .subscribe((response: any) => {
          if (response && response.success && response.data) {
            this.deviceTypes = response.data;
            this.didSelectDeviceModal();
          } else {
            const header = 'Error';
            this.uiUtilityService.showAlert(response.errorMessage!, header);
          }
        });
    }
  }

  async didManageDevice() {
    const payload = this.deviceDetailsForm?.value;
    payload.providerId = this.serviceId;
    if (this.f['placementType']?.value === 'bed') {
      payload['roomId'] = null;
      payload['roomName'] = null;
      payload['bedSlotId'] = null;
      payload['slotName'] = null;
    }
    if (this.f['placementType']?.value === 'bedslot') {
      payload['bedId'] = null;
      payload['bedName'] = null;
    }
    if (!this.f['placementType']?.value) {
      payload['roomId'] = null;
      payload['roomName'] = null;
      payload['bedSlotId'] = null;
      payload['slotName'] = null;
      payload['bedId'] = null;
      payload['bedName'] = null;
    }
    if (this.macId) {
      payload.macId = this.macId;
      await this.uiUtilityService.showLoader();
      this.deviceService.updateDevice(payload).subscribe((response: any) => {
        if (response && response.success && response.data?.macId) {
          this.uiUtilityService.showToast('Device details updated.', TOAST.success);
          this.navigateBack();
        } else {
          const header = 'Error';
          this.uiUtilityService.showAlert(response.errorMessage!, header);
        }
      });
    } else {
      this.uiUtilityService.showLoader();
      this.deviceService.createDevice(payload).subscribe((response: any) => {
        if (response && response.success && response.data?.macId) {
          this.uiUtilityService.showToast('Device added.', TOAST.success);
          this.deviceDetailsForm?.markAsPristine();
          this.didOnInit(response.data.macId, 'detail');
          this.didViewWillEnter();
        } else {
          const header = 'Error';
          this.uiUtilityService.showAlert(response.errorMessage!, header);
        }
      });
    }
  }

  manageDevice() {
    if (this.macId && this.isReadMode) {
      this.nav.push(DeviceDetailsPage, { serviceId: this.serviceId, serviceName: this.serviceName, macId: this.macId, mode: 'edit' });
      return;
    }
    if (this.deviceDetailsForm?.invalid) {
      return;
    }
    if (this.f['roomId']?.value && !this.f['bedSlotId']?.value) {
      this.uiUtilityService.showAlert('Please select the bed slot and proceed.', 'Error');
      return;
    }
    let isConfirm = false;
    if (this.f['placementType']?.value === 'bed') {
      isConfirm = this.select.bedMacId && this.f['macId']?.value && this.select.bedMacId !== this.f['macId'].value;
    }
    if (this.f['placementType']?.value === 'bedslot') {
      isConfirm = this.select.bedSlot?.macId && this.f['macId']?.value && this.select.bedSlot.macId !== this.f['macId'].value;
    }
    if (isConfirm) {
      const message =
        `Are you sure you want to assign this device here? The existing device at this location will be replaced with this device.`;
      const header = 'Confirm';
      this.uiUtilityService.presentConfirm(message, () => this.didManageDevice(), undefined, header, 'Replace Device');
    } else {
      this.didManageDevice();
    }
  }

  navigateBack(isRestrict = false) {
    if (!(this.macId && isRestrict && !this.deviceDetailsForm?.pristine)) {
      this.nav.pop();
    }
  }

  onAssignRoomToBed = async (room: any) => {
    this.deviceDetailsForm?.controls['roomName'].patchValue(
      room.roomName
    );
    this.deviceDetailsForm?.controls['roomId'].patchValue(
      room.id
    );
    this.deviceDetailsForm?.controls['slotName'].patchValue(null);
    this.deviceDetailsForm?.controls['bedSlotId'].patchValue(null);
    this.deviceDetailsForm?.markAsDirty();
    this.deviceDetailsForm?.updateValueAndValidity();
    this.select['roomId'] = room.id;
    this.select['bedSlot'] = { bedSlotId: null };
  }

  assignRoomToBed(room: any) {
    if (room?.id && this.deviceDetails?.roomId && room.id === this.deviceDetails.roomId) {
      return;
    }
    // if (this.deviceDetails?.roomId) {
    //   const message =
    //     'Please note changing or removing the room will clear the resident selection.';
    //   const header = 'Confirm';
    //   this.uiUtilityService.presentConfirm(message, () => this.onAssignRoomToBed(room), undefined, header, 'Ok');
    // } else {
    //   this.onAssignRoomToBed(room);
    // }
    this.onAssignRoomToBed(room);
  }

  onRemoveRoomToBed = async () => {
    this.deviceDetailsForm?.controls['roomName'].patchValue(null);
    this.deviceDetailsForm?.controls['roomId'].patchValue(null);
    this.deviceDetailsForm?.controls['slotName'].patchValue(null);
    this.deviceDetailsForm?.controls['bedSlotId'].patchValue(null);
    this.deviceDetailsForm?.markAsDirty();
    this.deviceDetailsForm?.updateValueAndValidity();
    this.select['roomId'] = null;
    this.select['bedSlot'] = { bedSlotId: null };
  }

  async didSelectRoomModal() {
    const roomId = this.select.hasOwnProperty('roomId') ? this.select.roomId : this.deviceDetails?.roomId;
    const { data, role } = await this.showSelectModal({ title: 'Select Room', emptyMessage: 'No rooms available right now.', key: 'id', displayName: 'roomName', value: roomId || '', list: this.rooms, isSave: false });
    if (role === 'confirm') {
      if (data !== '') {
        const room = this.rooms.find((item: any) => item.id === data);
        if (room) {
          this.assignRoomToBed(room);
        }
      } else {
        // const message =
        //   'Please note changing or removing the room will clear the resident selection.';
        // const header = 'Confirm';
        // this.uiUtilityService.presentConfirm(message, this.onRemoveRoomToBed, undefined, header, 'Ok');
        this.onRemoveRoomToBed();
      }
    }
  }

  async setRoom() {
    if (this.macId && this.isReadMode) {
      if (this.f['roomName']?.value && this.deviceDetails?.roomId && this.isRoomSummaryEnabled) {
        this.nav.push(RoomDetailsPage, { id: this.deviceDetails?.roomId });
      }
      return;
    }
    if (this.rooms.length) {
      this.didSelectRoomModal();
    } else {
      await this.uiUtilityService.showLoader();
      this.settingsService.getBedRoomList().subscribe((response: any) => {
        if (response && response.success && response.data) {
          const none = {
            id: '',
            roomName: 'None'
          };
          this.rooms = response.data?.length ? [none, ...response.data] : [];
          this.didSelectRoomModal();
        } else {
          const header = 'Error';
          this.uiUtilityService.showAlert(response.errorMessage!, header);
        }
      });
    }
  }

  onAssignToBed = (bed: any) => {
    this.deviceDetailsForm?.controls['bedName'].patchValue(
      bed.bedName
    );
    this.deviceDetailsForm?.controls['bedId'].patchValue(bed.bedId);
    this.deviceDetailsForm?.markAsDirty();
    this.deviceDetailsForm?.updateValueAndValidity();
    this.select['bedId'] = bed.bedId;
    this.select['bedName'] = bed.bedName;
    this.select['bedMacId'] = bed.macId;
  }

  onRemoveToBed = () => {
    this.deviceDetailsForm?.controls['bedName'].patchValue(null);
    this.deviceDetailsForm?.controls['bedId'].patchValue(null);
    this.deviceDetailsForm?.markAsDirty();
    this.deviceDetailsForm?.updateValueAndValidity();
    this.select['bedId'] = null;
    this.select['bedName'] = null;
    this.select['bedMacId'] = null;
  }

  async didSelectBedModal() {
    const bedId = this.select.hasOwnProperty('bedId') ? this.select.bedId : this.deviceDetails?.bedId;
    const { data, role } = await this.showSelectModal({ title: 'Select Bed', emptyMessage: 'No beds available right now.', key: 'bedId', displayName: 'bedName', value: bedId || '', list: this.bedList, isSave: false, rightSlot: { title: 'macId', titleFallback: 'Unassigned' } });
    if (role === 'confirm') {
      if (data !== '') {
        const bed = this.bedList.find((item: any) => item.bedId === data);
        if (bed) {
          this.onAssignToBed(bed);
        }
      } else {
        // const message =
        //   'Please note changing or removing the room will clear the resident selection.';
        // const header = 'Confirm';
        // this.uiUtilityService.presentConfirm(message, this.onRemoveToBed, undefined, header, 'Ok');
        this.onRemoveToBed();
      }
    }
  }

  async setBed() {
    if (this.macId && this.isReadMode) {
      if (this.f['bedName']?.value && this.deviceDetails?.bedId && this.isBedSummaryEnabled) {
        this.nav.push(BedDetailsPage, { bedId: this.deviceDetails.bedId, mode: 'detail' });
      }
      return;
    }
    if (this.bedList.length) {
      this.didSelectBedModal();
    } else {
      await this.uiUtilityService.showLoader();
      this.deviceService.getBedList(this.serviceId).subscribe((response: any) => {
        if (response && response.success && response.data) {
          const none = {
            id: '',
            bedName: 'None',
          };
          this.bedList = response.data?.length ? [none, ...response.data] : [];
          this.didSelectBedModal();
        } else {
          const header = 'Error';
          this.uiUtilityService.showAlert(response.errorMessage!, header);
        }
      });
    }
  }

  async didSelectBedSlotModal(bedSlots: any[]) {
    const bedSlotId = this.select.hasOwnProperty('bedSlot') && this.select.bedSlot?.bedSlotId ? this.select.bedSlot.bedSlotId : this.deviceDetails?.bedSlotId;
    const { data, role } = await this.showSelectModal({ title: 'Select Bed Slot', emptyMessage: 'No bed slots available right now.', key: 'bedSlotId', displayName: 'slotName', value: bedSlotId || '', list: bedSlots, isSave: false, header: this.f['roomName']?.value, rightSlot: { title: 'macId', titleFallback: 'Unassigned' } });
    if (role === 'confirm') {
      let slot = null;
      let slotId = null;
      if (data !== '') {
        const bedSlot = bedSlots.find((item: any) => item.bedSlotId === data);
        if (bedSlot) {
          this.select['bedSlot'] = bedSlot;
          slot = bedSlot.slotName;
          slotId = bedSlot.bedSlotId;
        }
      } else {
        this.select['bedSlot'] = {
          bedSlotId: null
        };
      }
      this.deviceDetailsForm?.controls['slotName'].patchValue(slot);
      this.deviceDetailsForm?.controls['bedSlotId'].patchValue(slotId);
      this.deviceDetailsForm?.markAsDirty();
      this.deviceDetailsForm?.updateValueAndValidity();
    }
  }

  async setBedSlot() {
    const roomN = this.select.hasOwnProperty('roomId') ? this.select.roomId : this.deviceDetails?.roomId;
    if (!roomN) {
      return;
    }
    await this.uiUtilityService.showLoader();
    this.deviceService.getBedRoomSlotList(roomN, this.serviceId).subscribe((response: any) => {
      if (response && response.success && response.data) {
        const none = {
          bedSlotId: '',
          slotName: 'None'
        };
        const pccOffMaxmBedSlotPerRoom = this.headerService.getCurrentFacility.pccOffMaxmBedSlotPerRoom;
        const bedSlotsList = response.data.slice(0, pccOffMaxmBedSlotPerRoom);
        const bedSlots = bedSlotsList?.length ? [none, ...bedSlotsList] : [];
        this.didSelectBedSlotModal(bedSlots);
      } else {
        const header = 'Error';
        this.uiUtilityService.showAlert(response.errorMessage!, header);
      }
    });
  }

  setResident() {
    if (this.f['residentName']?.value && this.deviceDetails?.residentId && this.isWearerSummaryEnabled) {
      this.nav.push(WearerDetailsPage, { id: this.deviceDetails?.residentId });
    }
  }

  dismiss() {
    if (this.uiUtilityService.pageModal) {
      this.dashboardService.refreshDashboardWithData.next(true);
      this.uiUtilityService.pageModal
        .dismiss()
        .then(() => this.menuCtrl.close());
      this.uiUtilityService.pageModal = null;
    }
  }

  onNavBack() {
    if (this.isRootPage) {
      this.dismiss();
    } else {
      this.nav.pop();
    }
  }

  onConfirmDeleteDevice = async () => {
    await this.uiUtilityService.showLoader();
    this.deviceService.deleteDevice(this.macId, this.serviceId).subscribe((response: any) => {
      if (response && response.success && response.data) {
        this.onNavBack();
        this.uiUtilityService.showToast('Device deleted.', TOAST.success);
      } else {
        const header = 'Error';
        this.uiUtilityService.showAlert(response.errorMessage!, header);
      }
    });
  };

  deleteDevice() {
    const message =
      'You are about to delete this device. This action cannot be undone.';
    const header = 'Delete This Device';
    this.uiUtilityService.presentConfirm(
      message,
      this.onConfirmDeleteDevice,
      undefined,
      header,
      'Delete'
    );
  }

  getConfig(config: UnifiedInput) {
    return { ...config };
  }

  get f() {
    return this.deviceDetailsForm?.controls || {};
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
